import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { Page, Component as BrComponent } from '@bloomreach/spa-sdk';
import { BaseProductDetailComponent } from '@products/base-product-detail.component';
import {
  FundAttributionCustomDataFetch,
  FundAttributionDownloadableGridData,
  FundAttributionPeriodDates,
  FundAttributionState,
  FundAttributionTimePeriods,
} from '@products/types/fund-attribution.type';
import { ProductDetailConfiguration } from '@types';
import { Logger } from '@utils/logger';
import { Subscription } from 'rxjs';
import { FundAttributionDisplayComponent } from '../fund-attribution-component.interface';
import { FundAttributionConfig } from '../fund-attribution.config';
import { FundAttributionService } from '../services/fund-attribution.service';
import cloneDeep from 'lodash/cloneDeep';
import { AppStateService, SignInService } from '@services';

const logger = Logger.getLogger('FundAttributionComponent');

@Component({
  selector: 'ft-fund-attribution',
  templateUrl: './fund-attribution.component.html',
  styleUrls: ['./fund-attribution.component.scss'],
})
export class FundAttributionComponent
  extends BaseProductDetailComponent
  implements FundAttributionDisplayComponent, OnDestroy {
  componentConfiguration: FundAttributionConfig;
  dataSubscription: Subscription;
  periodDates: FundAttributionPeriodDates[];
  timePeriods: FundAttributionTimePeriods;
  calendarYears: FundAttributionPeriodDates[];
  showCurrencyAttribution: boolean;
  fundName: string;
  bmName: string;
  isLoaded: boolean;
  isValidSignIn: boolean;
  brConfig: ProductDetailConfiguration;
  selectedAsOfDateStd: string;
  selectedGridDataType: string;
  public isAuth0Login: boolean;

  constructor(
    private fundAttributionService: FundAttributionService,
    private appStateService: AppStateService,
    private signInService: SignInService,
    private changeDetector: ChangeDetectorRef
  ) {
    super();
  }

  /**
   * Initialize attribution component and fetch & assign state.
   * @param componentConfig component configuration for attribution from BR
   * @param brConfig product basic info from BR
   * @param component BR component interface
   * @param page BR page interface
   */
  populate(
    componentConfig: FundAttributionConfig,
    brConfig: ProductDetailConfiguration,
    component: BrComponent,
    page: Page
  ): void {
    this.initialize(brConfig, component, page);
    logger.debug(page, this.page);
    this.isAuth0Login = this.appStateService.isAuth0Login();

    this.componentConfiguration = componentConfig;
    this.brConfig = brConfig;

    if (this.brConfig.fundId) {
      this.dataSubscription = this.fundAttributionService
        .getFundAttributionState$()
        .subscribe(
          (state: FundAttributionState) => {
            if (state.hasLoadedFullFeed) {
              // Assign successful sign in flag.
              this.isValidSignIn = state.validSignIn;

              if (state.validSignIn && state.timePeriods) {
                // Assign timeperiod dropdown dates.
                this.periodDates = state.periodDates;
                this.calendarYears = state.calendarYears;
                this.selectedAsOfDateStd = cloneDeep(state.selectedAsOfDateStd);

                // Assign timeperiod grid data values.
                this.timePeriods = cloneDeep({
                  quarterly: state.timePeriods.quarterly,
                  twelveMonthRolling: state.timePeriods.twelveMonthRolling,
                  ytd: state.timePeriods.ytd,
                  calendarYear: state.timePeriods.calendarYear,
                });

                // Assign default/user-defined action state.
                this.selectedGridDataType = cloneDeep(
                  state.selectedGridDataType
                );
                this.showCurrencyAttribution = cloneDeep(
                  state.showCurrencyAttribution
                );

                // Assign basic fund info.
                this.fundName = state.fundName;
                this.bmName = state.bmName;
              }
            }

            this.isLoaded = state.hasLoadedFullFeed;
            this.changeDetector.detectChanges();
          },
          (error) => {
            logger.error('Error in loading attribution data: ', error);

            this.isLoaded = false;
            this.changeDetector.detectChanges();
          }
        );

      this.fundAttributionService.populate(brConfig);
    }
  }

  /**
   * Invoke custom attribution data as per period date and view selected.
   * @param fundAttributionCustomDataFetch grid data action items for custom data fetch
   */
  fetchAttributionData(
    fundAttributionCustomDataFetch: FundAttributionCustomDataFetch
  ): void {
    this.fundAttributionService.populate(
      this.brConfig,
      fundAttributionCustomDataFetch
    );
  }

  /**
   * Trigger download action for the grid data.
   * @param downloadableGridData sector/country data for selected time period
   */
  onDownloadButtonClicked(
    downloadableGridData: FundAttributionDownloadableGridData
  ): void {
    this.fundAttributionService.triggerDownloadAction(downloadableGridData);
  }

  public goToAuth0() {
    this.signInService.submitAuth0();
  }

  ngOnDestroy(): void {
    // clean up subscription to avoid memory leaks
    this.dataSubscription?.unsubscribe();
  }
}
